/**
 * SmartSearchBar — universal FoodPulse search.
 *
 * One input, four inputs mode:
 *   • Text search — debounced autocomplete against /api/food-items
 *   • Voice search — Web Speech Recognition API (Chrome/Edge/Safari on iOS)
 *   • Barcode scan — opens <ProductScannerModal /> in Camera mode
 *   • Photo scan — opens <ProductScannerModal /> in Photo (AI) mode
 *
 * On match: navigates to /food-pulse/food/{slug}. On barcode match the
 * scanner modal already handles navigation via onMatch, but we route
 * it here to /food-pulse/food/{slug} to keep the flow consistent with
 * text picks.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Camera, Mic, MicOff, Sparkles, Loader2, ArrowRight, X } from "lucide-react";
import { http, API } from "@/lib/api";
import ProductScannerModal from "@/components/ProductScannerModal";

const GREEN = "#0F3D2E";
const AMBER = "#E4A140";

// Feature-detect Web Speech
const SR = (typeof window !== "undefined") && (window.SpeechRecognition || window.webkitSpeechRecognition);

export default function SmartSearchBar({ compact = false, autoFocus = false, placeholder }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerStartTab, setScannerStartTab] = useState("camera");
  const [listening, setListening] = useState(false);
  const inputRef = useRef();
  const timer = useRef();
  const recRef = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setItems([]); return; }
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await http.get("/food-items", { params: { q: q.trim(), limit: 8 } });
        setItems(r.data?.items || r.data || []);
      } catch { setItems([]); }
      setBusy(false);
    }, 280);
    return () => clearTimeout(timer.current);
  }, [q]);

  const gotoProduct = (slug) => {
    setItems([]); setQ("");
    nav(`/food-pulse/food/${slug}`);
  };

  const startVoice = () => {
    if (!SR) return;
    try {
      const rec = new SR();
      rec.continuous = false; rec.interimResults = false; rec.lang = "en-US";
      rec.onresult = (e) => {
        const transcript = e.results?.[0]?.[0]?.transcript || "";
        setQ(transcript); setListening(false);
      };
      rec.onend = () => setListening(false);
      rec.onerror = () => setListening(false);
      rec.start();
      setListening(true);
      recRef.current = rec;
    } catch { setListening(false); }
  };
  const stopVoice = () => { try { recRef.current?.stop(); } catch { /* ignore */ } setListening(false); };

  const onScanMatch = (product) => {
    if (product?.slug) gotoProduct(product.slug);
  };

  return (
    <>
      <div className="relative w-full" data-testid="smart-search">
        <div className={`flex items-center gap-1.5 rounded-full border-2 bg-white/95 backdrop-blur ${compact ? "px-2 py-1.5" : "px-3 py-2.5"} shadow-sm`}
             style={{ borderColor: AMBER }}>
          <Search className={`opacity-60 flex-shrink-0 ${compact ? "w-4 h-4" : "w-5 h-5"}`} style={{ color: GREEN }} />
          <input
            ref={inputRef}
            autoFocus={autoFocus}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && items[0]) gotoProduct(items[0].slug);
            }}
            placeholder={placeholder || "Search products, brands, deals, recipes…"}
            className="flex-1 outline-none bg-transparent text-sm min-w-0"
            style={{ color: GREEN }}
            data-testid="smart-search-input"
          />
          {busy && <Loader2 className="w-4 h-4 animate-spin opacity-50 flex-shrink-0" />}
          {q && (
            <button onClick={() => { setQ(""); setItems([]); }} className="opacity-50 hover:opacity-100 flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          )}
          <div className="flex items-center gap-0.5 border-l pl-1.5 ml-0.5 flex-shrink-0" style={{ borderColor: "#EED9AC" }}>
            {SR && (
              <button
                onClick={listening ? stopVoice : startVoice}
                className={`p-1.5 rounded-full hover:bg-emerald-50 ${listening ? "text-red-600 animate-pulse" : ""}`}
                title={listening ? "Listening…" : "Voice search"}
                data-testid="smart-search-voice"
              >
                {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
            )}
            <button
              onClick={() => { setScannerStartTab("camera"); setScannerOpen(true); }}
              className="p-1.5 rounded-full hover:bg-emerald-50"
              title="Scan a barcode"
              data-testid="smart-search-barcode"
            >
              <Camera className="w-4 h-4" />
            </button>
            <button
              onClick={() => { setScannerStartTab("photo"); setScannerOpen(true); }}
              className="p-1.5 rounded-full hover:bg-emerald-50"
              title="Identify by photo (AI)"
              data-testid="smart-search-photo"
            >
              <Sparkles className="w-4 h-4" />
            </button>
          </div>
        </div>

        {items.length > 0 && (
          <ul className="absolute z-40 top-full mt-2 left-0 right-0 bg-white rounded-2xl border shadow-2xl overflow-hidden"
              style={{ borderColor: "#E9D8B4" }} data-testid="smart-search-results">
            {items.slice(0, 8).map((it) => (
              <li key={it.slug || it.id}>
                <button
                  onClick={() => gotoProduct(it.slug)}
                  className="w-full text-left px-3 py-2 hover:bg-amber-50 flex items-center gap-3"
                  data-testid={`smart-search-hit-${it.slug}`}
                >
                  {(it.hero_image || it.image_url) ? (
                    <img src={it.hero_image || it.image_url} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded-lg bg-amber-100 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate" style={{ color: GREEN }}>{it.name || it.common_name || it.slug}</div>
                    <div className="text-xs opacity-60 truncate">{it.category?.replace(/-/g, " ")}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 opacity-40 flex-shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ProductScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onMatch={onScanMatch}
        initialMode={scannerStartTab}
      />
    </>
  );
}
